/**
 * Integrytas Group - Read-Optimization Router (ROR)
 * Page: ROR.ROUTING
 *
 * Manage read-routing rules (which read-heavy surfaces use the replica/_unified
 * tier), test the resolver, and inspect the read-after-write freshness tracker.
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedUpdate, routedDelete } from '../../lib/routed_writes';
import type { AuthSession } from '../../types/auth';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'ROR.ROUTING';

interface RuleRow {
  ror_route_rule_id: number; internal_code: string; name: string | null; entity_name: string;
  screen_code: string | null; workspace_code: string; tier: 'PRIMARY' | 'REPLICA'; is_active: 'Y' | 'N';
}
interface FreshRow { entity_name: string; record_pk: string; workspace_code: string; written_at: string }

const RULE_GRID_COLS: GridColumnDef[] = [
  { key: 'internal_code',  header: 'Code',      defaultWidth: 180 },
  { key: 'entity_name',    header: 'Entity',    defaultWidth: 180 },
  { key: 'screen_code',    header: 'Screen',    defaultWidth: 160 },
  { key: 'workspace_code', header: 'Workspace', defaultWidth: 130 },
  { key: 'tier',           header: 'Tier',      defaultWidth: 120 },
  { key: 'is_active',      header: 'On',        defaultWidth: 60  },
];

export interface RorRoutingPageProps { session: AuthSession }

export function RorRoutingPage({ session }: RorRoutingPageProps) {
  return (
    <PersonalizationProvider pageCode="ROR.ROUTING" session={session}>
      <RorRoutingPageInner session={session} />
    </PersonalizationProvider>
  );
}

function RorRoutingPageInner({ session }: RorRoutingPageProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canAdd = hasAccess(session, PAGE_CODE, 'add');
  const canDelete = hasAccess(session, PAGE_CODE, 'delete');

  const { t } = useTranslation();
  const [rules, setRules] = useState<RuleRow[]>([]);
  const [fresh, setFresh] = useState<FreshRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  // §17.B5: per-column multi-value facets for the route-rule list.
  const [facets, setFacets] = useState<{ tier: string[]; active: string[] }>({ tier: [], active: ['Y'] });
  // resolve tester
  const [rt, setRt] = useState({ table: '', screen: '', ws: '', pk: '', strong: false });
  const [resolved, setResolved] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const [r, f] = await Promise.all([
        apiDb.from('ror_route_rule').select('ror_route_rule_id, internal_code, name, entity_name, screen_code, workspace_code, tier, is_active').order('internal_code'),
        apiDb.from('ror_freshness').select('entity_name, record_pk, workspace_code, written_at').order('written_at', { ascending: false }).limit(50),
      ]);
      if (r.error) throw r.error;
      setRules((r.data ?? []) as RuleRow[]);
      setFresh((f.data ?? []) as FreshRow[]);
    } catch (err) { setError((err as Error).message); } finally { setBusy(false); }
  };
  useEffect(() => { void reload(); }, []);

  const facetOptions = useMemo(() => ({
    tier:   [{ value: 'REPLICA', label: 'REPLICA' }, { value: 'PRIMARY', label: 'PRIMARY' }],
    active: [{ value: 'Y', label: t('ror.on', 'On') }, { value: 'N', label: t('ror.off', 'Off') }],
  }), [t]);

  const facetsActive = facets.tier.length + facets.active.length > 0;
  const anyFilterActive = facetsActive || filter.trim().length > 0;
  const clearAll = () => { setFilter(''); setFacets({ tier: [], active: [] }); };

  const filtered = useMemo(() => {
    // A row passes a facet when no values are selected, or it matches one (OR
    // within a facet, AND across facets).
    const matchesFacets = (r: RuleRow) =>
      (facets.tier.length   === 0 || facets.tier.includes(r.tier)) &&
      (facets.active.length === 0 || facets.active.includes(r.is_active));
    const q = filter.trim().toLowerCase();
    const base = !q ? rules : rules.filter(r =>
      r.internal_code.toLowerCase().includes(q) ||
      (r.name ?? '').toLowerCase().includes(q) ||
      r.entity_name.toLowerCase().includes(q) ||
      (r.screen_code ?? '').toLowerCase().includes(q) ||
      r.workspace_code.toLowerCase().includes(q));
    return base.filter(matchesFacets);
  }, [rules, filter, facets]);

  // §17.B13: expand/contract related-children. A route rule has no loaded child
  // collection, so the control renders inert.
  const detail = useExpandedRows<number>();
  // TODO: wire relations — a route rule references entity/screen but loads no children.
  const buildRelatedGroups = (): RelatedChildGroup[] => [];

  const addRule = async () => {
    await routedInsert(session, 'ror_route_rule', { internal_code: `ROUTE_${Date.now()}`, name: 'New route', entity_name: '*', screen_code: null, workspace_code: '', tier: 'REPLICA', is_active: 'N' });
    await reload();
  };
  const saveRule = async (r: RuleRow) => {
    const { error: e } = await routedUpdate(session, 'ror_route_rule', 'ror_route_rule_id', r.ror_route_rule_id, {
      internal_code: r.internal_code.trim(), name: r.name?.trim() || null, entity_name: r.entity_name.trim() || '*',
      screen_code: r.screen_code?.trim() || null, workspace_code: r.workspace_code.trim(), tier: r.tier, is_active: r.is_active,
    });
    if (e) setError(e.message);
  };
  const delRule = async (id: number) => { await routedDelete(session, 'ror_route_rule', 'ror_route_rule_id', id); setRules(rs => rs.filter(x => x.ror_route_rule_id !== id)); };
  const patch = (i: number, p: Partial<RuleRow>) => setRules(rs => rs.map((x, j) => j === i ? { ...x, ...p } : x));

  const runResolve = async () => {
    setBusy(true); setError(null); setResolved(null);
    try {
      const { data, error: e } = await apiDb.rpc('ror_resolve', { p_table: rt.table.trim(), p_screen_code: rt.screen.trim() || null, p_workspace_code: rt.ws.trim(), p_record_pk: rt.pk.trim() || null, p_strong: rt.strong });
      if (e) throw e;
      setResolved(JSON.stringify(data));
    } catch (err) { setError((err as Error).message); } finally { setBusy(false); }
  };

  const now = Date.now();

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-csm-border bg-white px-3">
        <span className="csm-page-title">{t('ror.title', 'Read Routing')}</span>
        <input
          type="text"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder={t('ror.filter_placeholder', 'Filter rules…')}
          className="h-7 w-60 rounded border border-csm-border px-2 text-csm-md focus:border-csm-watermark focus:outline-none"
        />
        <FacetFilter label={t('ror.col.tier', 'Tier')}   options={facetOptions.tier}   selected={facets.tier}   onChange={v => setFacets(f => ({ ...f, tier: v }))} />
        <FacetFilter label={t('ror.col.is_active', 'On')} options={facetOptions.active} selected={facets.active} onChange={v => setFacets(f => ({ ...f, active: v }))} />
        <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
        <button onClick={() => void reload()} className="h-7 rounded border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt">{t('ror.refresh', 'Refresh')}</button>
        {canAdd && (<button onClick={() => void addRule()} className="h-7 rounded border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt">{t('ror.add_rule', '+ Rule')}</button>)}
        {busy && <span className="text-csm-xs text-csm-watermark">{t('ror.working', 'Working…')}</span>}
        <div className="ml-auto"><PersonalizationToggle /></div>
      </div>

      <div className="flex-1 overflow-auto p-3">
        {error && (
          <ErrorBanner
            message={t('ror.load_failed', "Couldn't complete the operation.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}
        <section className="mb-5">
          <h3 className="mb-2 text-csm-sm font-semibold text-csm-ink">{t('ror.section.route_rules', 'Route Rules')} <span className="text-csm-2xs text-csm-watermark">{t('ror.section.route_rules_hint', '(a matching rule routes reads to the replica _unified tier when that view exists)')}</span></h3>
          <div className="overflow-x-auto">
            <PersonalizedGrid componentKey="grid" columns={RULE_GRID_COLS}>
              {(visibleCols, widths) => (
                <table className="w-max min-w-full border-collapse text-csm-sm">
                  <thead><tr className="border-b border-csm-border text-left text-csm-2xs uppercase tracking-wide text-csm-watermark">
                    <th className="w-7 px-1 py-1" title={t('common.related.header', 'Related records')} />
                    {visibleCols.map(col => (
                      <th key={col.key} className="px-1 py-1" style={widths[col.key] ? { width: widths[col.key] } : undefined}>{t('ror.col.' + col.key, col.header)}</th>
                    ))}
                    <th className="px-1 py-1"></th>
                  </tr></thead>
                  <tbody>
                    {filtered.map(r => {
                      const i = rules.indexOf(r);
                      return (
                        <Fragment key={r.ror_route_rule_id}>
                        <tr className="border-b border-csm-border-soft">
                          <td className="px-1 py-1 text-center align-middle" onClick={e => e.stopPropagation()}>
                            <RelatedExpandButton open={detail.isOpen(r.ror_route_rule_id)} hasChildren={false} onToggle={() => detail.toggle(r.ror_route_rule_id)} expandLabel={t('common.related.show', 'Show related records')} collapseLabel={t('common.related.hide', 'Hide related')} tooltip={t('common.related.tooltip', 'Show related records')} />
                          </td>
                          {visibleCols.map(col => (
                            <td key={col.key} className={`px-1 py-1${col.key === 'is_active' ? ' text-center' : ''}`}
                                style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                              {col.key === 'internal_code'  && <input value={r.internal_code} onChange={e => patch(i, { internal_code: e.target.value })} onBlur={() => void saveRule(rules[i])} className="h-7 w-40 rounded border border-csm-border px-1 font-mono text-csm-xs" />}
                              {col.key === 'entity_name'    && <input value={r.entity_name} onChange={e => patch(i, { entity_name: e.target.value })} onBlur={() => void saveRule(rules[i])} placeholder={t('ror.placeholder.entity', '* = any')} className="h-7 w-40 rounded border border-csm-border px-1 font-mono text-csm-2xs" />}
                              {col.key === 'screen_code'    && <input value={r.screen_code ?? ''} onChange={e => patch(i, { screen_code: e.target.value })} onBlur={() => void saveRule(rules[i])} placeholder={t('ror.placeholder.screen', 'any')} className="h-7 w-36 rounded border border-csm-border px-1 font-mono text-csm-2xs" />}
                              {col.key === 'workspace_code' && <input value={r.workspace_code} onChange={e => patch(i, { workspace_code: e.target.value })} onBlur={() => void saveRule(rules[i])} placeholder={t('ror.placeholder.workspace', 'all')} className="h-7 w-28 rounded border border-csm-border px-1 font-mono text-csm-2xs" />}
                              {col.key === 'tier'           && <select value={r.tier} onChange={e => patch(i, { tier: e.target.value as RuleRow['tier'] })} onBlur={() => void saveRule(rules[i])} className="h-7 rounded border border-csm-border px-1 text-csm-2xs">{['REPLICA', 'PRIMARY'].map(x => <option key={x} value={x}>{x}</option>)}</select>}
                              {col.key === 'is_active'      && <input type="checkbox" checked={r.is_active === 'Y'} onChange={e => patch(i, { is_active: e.target.checked ? 'Y' : 'N' })} onBlur={() => void saveRule(rules[i])} />}
                            </td>
                          ))}
                          <td className="px-1 py-1">{canDelete && (<button onClick={() => void delRule(r.ror_route_rule_id)} className="text-csm-xs text-csm-danger">{t('ror.del', 'del')}</button>)}</td>
                        </tr>
                        {detail.isOpen(r.ror_route_rule_id) && (
                          <tr><td colSpan={visibleCols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}><RelatedChildrenPanel groups={buildRelatedGroups()} session={session} /></td></tr>
                        )}
                        </Fragment>
                      );
                    })}
                    {filtered.length === 0 && (
                      <tr><td colSpan={visibleCols.length + 2} className="px-2 py-3">
                        <EmptyState variant={anyFilterActive ? 'no-match' : 'no-data'} title={t('ror.empty.rules', 'No route rules — all reads serve from PRIMARY. Add a rule to route a read-heavy screen/entity to the replica.')} action={!anyFilterActive ? canAdd && (<button onClick={() => void addRule()} className="rounded bg-primary px-3 py-1.5 text-csm-sm text-white hover:bg-primary-hover">{t('ror.add_rule', '+ Rule')}</button>) : undefined} />
                      </td></tr>
                    )}
                  </tbody>
                </table>
              )}
            </PersonalizedGrid>
          </div>
        </section>

        <section className="mb-5">
          <h3 className="mb-2 text-csm-sm font-semibold text-csm-ink">{t('ror.section.resolver_test', 'Resolver Test')}</h3>
          <div className="flex flex-wrap items-end gap-2">
            <Field label={t('ror.field.table', 'table')} v={rt.table} on={v => setRt({ ...rt, table: v })} />
            <Field label={t('ror.field.screen_code', 'screen_code')} v={rt.screen} on={v => setRt({ ...rt, screen: v })} />
            <Field label={t('ror.field.workspace', 'workspace')} v={rt.ws} on={v => setRt({ ...rt, ws: v })} />
            <Field label={t('ror.field.record_pk', 'record_pk')} v={rt.pk} on={v => setRt({ ...rt, pk: v })} />
            <label className="flex items-center gap-1 text-csm-xs"><input type="checkbox" checked={rt.strong} onChange={e => setRt({ ...rt, strong: e.target.checked })} /> {t('ror.strong', 'strong')}</label>
            <button onClick={() => void runResolve()} className="h-7 rounded border border-primary bg-primary px-3 text-csm-xs text-white hover:opacity-90">{t('ror.resolve', 'Resolve')}</button>
            {resolved && <span className="font-mono text-csm-xs text-csm-ink">{resolved}</span>}
          </div>
        </section>

        <section>
          <h3 className="mb-2 text-csm-sm font-semibold text-csm-ink">{t('ror.section.recent_writes', 'Recent Writes')} <span className="text-csm-2xs text-csm-watermark">{t('ror.section.recent_writes_hint', '(read-after-write freshness — reads of these route to PRIMARY within the TTL)')}</span></h3>
          <table className="w-full max-w-3xl border-collapse text-csm-sm">
            <thead><tr className="border-b border-csm-border text-left text-csm-2xs uppercase tracking-wide text-csm-watermark">
              <th className="px-2 py-1">{t('ror.writes.entity', 'Entity')}</th><th className="px-2 py-1">{t('ror.writes.record', 'Record')}</th><th className="px-2 py-1">{t('ror.writes.workspace', 'Workspace')}</th><th className="px-2 py-1">{t('ror.writes.written', 'Written')}</th>
            </tr></thead>
            <tbody>
              {fresh.map(w => (
                <tr key={`${w.entity_name}:${w.record_pk}:${w.workspace_code}`} className="border-b border-csm-border-soft">
                  <td className="px-2 py-1 font-mono text-csm-xs">{w.entity_name}</td>
                  <td className="px-2 py-1 font-mono text-csm-xs">{w.record_pk}</td>
                  <td className="px-2 py-1 text-csm-xs">{w.workspace_code || '—'}{/* fk-display-ok: ASF workspace technical key, no named entity */}</td>
                  <td className="px-2 py-1 text-csm-2xs text-csm-watermark">{t('ror.writes.seconds_ago', '{{n}}s ago', { n: Math.round((now - new Date(w.written_at).getTime()) / 1000) })}</td>
                </tr>
              ))}
              {fresh.length === 0 && (
                <tr><td colSpan={4} className="px-2 py-3">
                  <EmptyState variant="no-data" title={t('ror.empty.writes', 'No recent writes tracked.')} />
                </td></tr>
              )}
            </tbody>
          </table>
        </section>
      </div>
    </div>
  );
}

function Field({ label, v, on }: { label: string; v: string; on: (v: string) => void }) {
  return (
    <label className="flex flex-col">
      <span className="text-csm-2xs uppercase tracking-wide text-csm-watermark">{label}</span>
      <input value={v} onChange={e => on(e.target.value)} className="h-7 w-32 rounded border border-csm-border px-1 font-mono text-csm-xs" />
    </label>
  );
}
